(() => {
  'use strict';
  if (window.__livearoDraperyBound) return;
  window.__livearoDraperyBound = true;

  const STORAGE_KEY = 'livearo-fabric-selections-v1';
  const all = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const language = document.documentElement.lang.toLowerCase().startsWith('zh') ? 'zh'
    : document.documentElement.lang.toLowerCase().startsWith('en') ? 'en' : 'ja';
  const local = value => typeof value === 'string' ? value : String(value?.[language] || value?.ja || value?.en || '');
  const normalize = value => String(value || '').normalize('NFKC').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
  const products = new Map();
  const series = new Map();
  const productSeries = new Map();
  const imageURLs = new Map();
  const searchValues = new Map();
  const libraryStates = new WeakMap();
  const currentVariants = new WeakMap();
  const boundDialogs = new WeakSet();
  const boundControls = new WeakSet();
  let selections = [];
  let statusTimer;
  let selectionObserver;

  function text(key, replacements = {}) {
    const entry = window.LIVEARO_DRAPERY?.ui?.[key];
    return local(entry || key).replace(/\{(\w+)\}/g, (match, name) => Object.prototype.hasOwnProperty.call(replacements, name) ? String(replacements[name]) : match);
  }

  function readData() {
    products.clear(); series.clear(); productSeries.clear(); searchValues.clear();
    const data = window.LIVEARO_DRAPERY;
    if (!Array.isArray(data?.products) || !Array.isArray(data?.series)) return;
    data.products.forEach(product => {
      if (typeof product?.id !== 'string' || !product.id || typeof product.code !== 'string') return;
      products.set(product.id, product);
    });
    data.series.forEach(group => {
      if (typeof group?.code !== 'string' || !Array.isArray(group.productIds)) return;
      const ids = [...new Set(group.productIds.filter(id => products.has(id)))];
      series.set(group.code, { ...group, productIds: ids });
      ids.forEach(id => productSeries.set(id, group.code));
    });
    products.forEach(product => {
      const group = series.get(productSeries.get(product.id));
      const details = (Array.isArray(product.details) ? product.details : []).flatMap(detail => [local(detail.label), local(detail.value)]);
      searchValues.set(product.id, normalize([product.code, product.series, group?.code, local(group?.title), ...details].join(' ')));
    });
    all('[data-fabric-variant]').forEach(button => {
      const id = button.dataset.fabricVariant;
      if (products.has(id) && button.dataset.fabricSrc) imageURLs.set(id, button.dataset.fabricSrc);
    });
  }

  function validSelections(value) {
    return Array.isArray(value) ? [...new Set(value.filter(id => typeof id === 'string' && products.has(id)))] : [];
  }

  function parseSelections(value) {
    try { return validSelections(JSON.parse(value || '[]')); }
    catch (_) { return []; }
  }

  function saveSelections() {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(selections)); }
    catch (_) { /* The current selection remains available without browser storage. */ }
  }

  function loadSelections() {
    try { return parseSelections(localStorage.getItem(STORAGE_KEY)); }
    catch (_) { return []; }
  }

  function announce(key, replacements = {}) {
    clearTimeout(statusTimer);
    all('[data-fabric-status]').forEach(node => {
      node.setAttribute('aria-live', 'polite');
      node.setAttribute('role', 'status');
      node.hidden = false;
      const message = text(key, replacements);
      node.textContent = replacements.code && !local(window.LIVEARO_DRAPERY?.ui?.[key]).includes('{code}')
        ? replacements.code + ' — ' + message : message;
    });
    statusTimer = window.setTimeout(() => {
      all('[data-fabric-status]').forEach(node => { node.textContent = ''; });
    }, 4500);
  }

  function replaceQuery(updates) {
    try {
      const url = new URL(location.href);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === '' || value === null || value === undefined) url.searchParams.delete(key);
        else url.searchParams.set(key, value);
      });
      if (url.href !== location.href) history.replaceState(history.state, '', url);
    } catch (_) { /* Embedded previews may disallow browser history changes. */ }
  }

  function matchesQuery(id, query) {
    const terms = String(query || '').normalize('NFKC').trim().split(/\s+/).map(normalize).filter(Boolean);
    const value = searchValues.get(id) || '';
    return terms.every(term => value.includes(term));
  }

  function applyFilter(library, updateURL = false) {
    const state = libraryStates.get(library);
    if (!state) return;
    state.query = state.input?.value || '';
    state.matches = new Map();
    let count = 0;
    all('[data-fabric-series]', library).forEach(card => {
      const code = card.dataset.fabricSeries;
      const group = series.get(code);
      const ids = (group?.productIds || []).filter(id => matchesQuery(id, state.query));
      state.matches.set(code, ids);
      const visible = ids.length > 0 && (state.filter === 'all' || state.filter === code);
      card.hidden = !visible;
      if (visible) count += ids.length;
      all('[data-fabric-series-count]', card).forEach(node => { node.textContent = String(ids.length); });
    });
    all('[data-fabric-filter]', library).forEach(button => {
      button.setAttribute('aria-pressed', String(button.dataset.fabricFilter === state.filter));
    });
    all('[data-fabric-count]', library).forEach(node => { node.textContent = String(count); });
    all('[data-fabric-empty]', library).forEach(node => { node.hidden = count > 0; });
    if (updateURL) replaceQuery({ filter: state.filter === 'all' ? null : state.filter, q: state.query.trim() || null });
  }

  function setVariant(dialog, requestedId, updateURL = false) {
    const group = series.get(dialog.dataset.fabricDialog);
    if (!group?.productIds.length) return;
    let id = requestedId || currentVariants.get(dialog);
    if (!group.productIds.includes(id)) id = group.productIds[0];
    const product = products.get(id);
    if (!product) return;
    currentVariants.set(dialog, id);
    all('[data-fabric-code]', dialog).forEach(node => { node.textContent = product.code; });
    const source = imageURLs.get(id);
    all('[data-fabric-image]', dialog).forEach(image => {
      if (source) {
        image.src = source;
        image.hidden = false;
      } else {
        image.removeAttribute('src');
        image.hidden = true;
      }
      image.removeAttribute('srcset');
      image.alt = [local(group.title), product.code].filter(Boolean).join(' / ');
      if (Number.isFinite(product.image?.width)) image.width = product.image.width;
      if (Number.isFinite(product.image?.height)) image.height = product.image.height;
    });
    all('[data-fabric-details]', dialog).forEach(list => {
      const fragment = document.createDocumentFragment();
      (Array.isArray(product.details) ? product.details : []).forEach(detail => {
        const label = local(detail.label);
        const value = local(detail.value);
        if (!label || !value) return;
        const row = document.createElement('div');
        row.className = 'fabric-detail-row';
        if (typeof detail.key === 'string') row.dataset.fabricDetail = detail.key;
        const term = document.createElement('dt'); term.textContent = label;
        const description = document.createElement('dd'); description.textContent = value;
        row.append(term, description); fragment.append(row);
      });
      list.replaceChildren(fragment);
    });
    all('[data-fabric-care]', dialog).forEach(list => {
      const fragment = document.createDocumentFragment();
      (Array.isArray(product.care) ? product.care : []).forEach(item => {
        const value = local(item);
        if (!value) return;
        const li = document.createElement('li'); li.textContent = value; fragment.append(li);
      });
      list.replaceChildren(fragment);
      list.hidden = !list.childNodes.length;
    });
    all('[data-fabric-lot-note]', dialog).forEach(node => {
      node.textContent = local(product.lotNote);
      node.hidden = !node.textContent;
    });
    all('[data-fabric-variant]', dialog).forEach(button => {
      button.setAttribute('aria-pressed', String(button.dataset.fabricVariant === id));
    });
    updateAddButtons(dialog);
    if (updateURL) replaceQuery({ fabric: id });
  }

  function updateAddButtons(dialog) {
    const id = currentVariants.get(dialog);
    const selected = selections.includes(id);
    all('[data-fabric-add]', dialog).forEach(button => {
      button.textContent = text(selected ? 'added' : 'add');
      button.disabled = !products.has(id) || selected;
      button.setAttribute('aria-pressed', String(selected));
    });
  }

  function updateInquiryLink(anchor) {
    if (!selections.length || !anchor.dataset.contactUrl) {
      anchor.removeAttribute('href');
      anchor.setAttribute('aria-disabled', 'true');
      return;
    }
    try {
      const url = new URL(anchor.dataset.contactUrl, location.href);
      url.searchParams.set('type', 'sample');
      url.searchParams.set('fabrics', JSON.stringify(selections));
      anchor.href = url.href;
      anchor.removeAttribute('aria-disabled');
    } catch (_) {
      anchor.removeAttribute('href');
      anchor.setAttribute('aria-disabled', 'true');
    }
  }


  function syncCombinedPreviewSelection() {
    if (document.body.dataset.preview !== 'true') return;
    const genericList = document.querySelector('[data-sample-list]');
    const genericCount = genericList ? genericList.children.length : 0;
    const colorCounter = document.querySelector('[data-color-selection-count]');
    const colorCount = Math.max(0, Number.parseInt(colorCounter?.textContent || '0', 10) || 0);
    const verticalSelections = typeof window.LIVEARO_VERTICAL_SELECTIONS === 'function' ? window.LIVEARO_VERTICAL_SELECTIONS() : [];
    const total = genericCount + colorCount + selections.length + verticalSelections.length;
    all('[data-sample-count]').forEach(node => {
      node.textContent = String(total);
      node.hidden = total === 0;
    });
    all('[data-sample-empty]').forEach(node => { node.hidden = total > 0; });
    all('[data-sample-inquiry]').forEach(anchor => {
      const href = anchor.getAttribute('href');
      if (!href) return;
      try {
        const url = new URL(href, location.href);
        if (selections.length) url.searchParams.set('fabrics', JSON.stringify(selections));
        else url.searchParams.delete('fabrics');
        if (verticalSelections.length) url.searchParams.set('verticalColors', JSON.stringify(verticalSelections));
        else url.searchParams.delete('verticalColors');
        if (anchor.href !== url.href) anchor.href = url.href;
      } catch (_) { /* Preserve the existing enquiry link if its URL is invalid. */ }
    });
  }

  function observeSelectionCounts() {
    if (document.body.dataset.preview !== 'true' || !window.MutationObserver) return;
    if (!selectionObserver) selectionObserver = new MutationObserver(syncCombinedPreviewSelection);
    selectionObserver.disconnect();
    all('[data-color-selection-count], [data-sample-list]').forEach(node => {
      selectionObserver.observe(node, { childList: true, characterData: true, subtree: true });
    });
  }

  function renderSelections() {
    all('[data-fabric-selection-count]').forEach(node => { node.textContent = String(selections.length); });
    all('[data-fabric-selection-empty]').forEach(node => {
      node.hidden = selections.length > 0;
      node.textContent = text('noSelection');
    });
    all('[data-fabric-selection-list]').forEach(list => {
      const fragment = document.createDocumentFragment();
      selections.forEach(id => {
        const product = products.get(id);
        if (!product) return;
        const item = document.createElement('li'); item.className = 'fabric-selection-item';
        const source = imageURLs.get(id);
        if (source) {
          const image = document.createElement('img'); image.className = 'fabric-selection-thumb';
          image.src = source; image.alt = ''; image.loading = 'lazy'; image.width = 52; image.height = 52;
          item.append(image);
        }
        const name = document.createElement('span'); name.className = 'fabric-selection-code'; name.textContent = product.code;
        const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'fabric-selection-remove';
        remove.dataset.fabricRemove = id; remove.textContent = text('remove');
        remove.setAttribute('aria-label', text('remove') + ' ' + product.code);
        remove.addEventListener('click', () => {
          selections = selections.filter(value => value !== id); saveSelections(); renderSelections();
          announce('removedStatus', { code: product.code, count: selections.length });
        });
        item.append(name, remove); fragment.append(item);
      });
      list.replaceChildren(fragment);
    });
    all('[data-fabric-clear]').forEach(button => {
      button.disabled = !selections.length;
      button.textContent = text('clear');
    });
    all('[data-fabrics-inquiry]').forEach(updateInquiryLink);
    all('[data-fabric-dialog]').forEach(updateAddButtons);
    syncCombinedPreviewSelection();
  }

  function prefillContact(root = document) {
    const requested = parseSelections(new URLSearchParams(location.search).get('fabrics'));
    if (!requested.length) return;
    all('[data-contact-product]', root).forEach(input => {
      if (input.dataset.fabricsPrefilled) return;
      const existing = String(input.value || '');
      const additions = requested.map(id => products.get(id)?.code).filter(Boolean).filter(code => {
        const escaped = code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return !(new RegExp('(^|[^A-Za-z0-9-])' + escaped + '($|[^A-Za-z0-9-])')).test(existing);
      }).map(code => [text('inquiryPrefix'), code].filter(Boolean).join(' '));
      if (additions.length) input.value = [existing, ...additions].filter(Boolean).join(' / ');
      input.dataset.fabricsPrefilled = 'true';
    });
  }

  function bind(root = document) {
    readData();
    selections = validSelections(selections);
    const libraries = root.matches?.('[data-fabric-library]') ? [root, ...all('[data-fabric-library]', root)] : all('[data-fabric-library]', root);
    libraries.forEach(library => {
      if (!libraryStates.has(library)) {
        const params = new URLSearchParams(location.search);
        const input = library.querySelector('[data-fabric-search]');
        const filter = series.has(params.get('filter')) ? params.get('filter') : 'all';
        if (input) input.value = params.get('q') || '';
        libraryStates.set(library, { input, filter, query: input?.value || '', matches: new Map() });
        input?.addEventListener('input', () => applyFilter(library, true));
        all('[data-fabric-filter]', library).forEach(button => {
          button.addEventListener('click', event => {
            event.preventDefault();
            const value = button.dataset.fabricFilter;
            libraryStates.get(library).filter = series.has(value) ? value : 'all';
            applyFilter(library, true);
          });
        });
      }
      applyFilter(library);
    });
    all('[data-fabric-dialog]').forEach(dialog => {
      if (!boundDialogs.has(dialog)) {
        boundDialogs.add(dialog);
        all('[data-fabric-variant]', dialog).forEach(button => {
          button.addEventListener('click', event => {
            event.preventDefault(); setVariant(dialog, button.dataset.fabricVariant, true);
          });
        });
        all('[data-fabric-add]', dialog).forEach(button => {
          button.addEventListener('click', event => {
            event.preventDefault();
            const id = currentVariants.get(dialog);
            if (!products.has(id) || selections.includes(id)) return;
            selections.push(id); saveSelections(); renderSelections();
            announce('selectedStatus', { code: products.get(id).code, count: selections.length });
          });
        });
        dialog.addEventListener('close', () => {
          const id = new URLSearchParams(location.search).get('fabric');
          if (productSeries.get(id) === dialog.dataset.fabricDialog) replaceQuery({ fabric: null });
        });
      }
      setVariant(dialog);
    });
    all('[data-fabric-clear]').forEach(button => {
      if (boundControls.has(button)) return;
      boundControls.add(button);
      button.addEventListener('click', event => {
        event.preventDefault(); selections = []; saveSelections(); renderSelections(); announce('clearedStatus', { count: 0 });
      });
    });
    all('[data-fabrics-inquiry]').forEach(anchor => {
      if (boundControls.has(anchor)) return;
      boundControls.add(anchor);
      anchor.addEventListener('click', event => {
        if (anchor.getAttribute('aria-disabled') === 'true') event.preventDefault();
      });
    });
    renderSelections();
    observeSelectionCounts();
    prefillContact(root);
  }

  function openFromURL() {
    const id = new URLSearchParams(location.search).get('fabric');
    if (!products.has(id)) return;
    const dialog = all('[data-fabric-dialog]').find(node => node.dataset.fabricDialog === productSeries.get(id));
    if (!dialog) return;
    setVariant(dialog, id);
    all('[data-fabric-dialog][open]').forEach(open => { if (open !== dialog) open.close(); });
    if (!dialog.open) {
      try { dialog.showModal(); } catch (_) { /* A disconnected theme-editor section cannot open a dialog. */ }
    }
  }

  function preserveNativeLanguageQuery(event) {
    const select = event.target.closest?.('[data-language-select]');
    const form = select?.form || (event.type === 'submit' && event.target.querySelector?.('[data-language-select]') ? event.target : null);
    if (!form || !document.querySelector('[data-fabric-library], [data-vertical-library], [data-contact-product]')) return;
    let input = form.querySelector('input[name="return_to"]');
    if (!input) { input = document.createElement('input'); input.type = 'hidden'; input.name = 'return_to'; form.append(input); }
    input.value = location.pathname + location.search + location.hash;
  }

  function start() {
    readData(); selections = loadSelections(); bind();
    document.addEventListener('click', event => {
      const trigger = event.target.closest?.('[data-fabric-open]');
      if (!trigger) return;
      const dialog = document.getElementById(trigger.dataset.dialogOpen || 'fabric-' + trigger.dataset.fabricOpen);
      if (!dialog?.matches('[data-fabric-dialog]')) return;
      const library = trigger.closest('[data-fabric-library]');
      const state = library && libraryStates.get(library);
      const matching = state?.matches.get(dialog.dataset.fabricDialog) || series.get(dialog.dataset.fabricDialog)?.productIds || [];
      const current = currentVariants.get(dialog);
      const id = matching.includes(current) ? current : matching[0];
      setVariant(dialog, id, true);
    }, true);
    document.addEventListener('livearo:generic-samples-updated', syncCombinedPreviewSelection);
    document.addEventListener('livearo:vertical-selection-updated', syncCombinedPreviewSelection);
    document.addEventListener('change', preserveNativeLanguageQuery, true);
    document.addEventListener('submit', preserveNativeLanguageQuery, true);
    document.addEventListener('shopify:section:load', event => { bind(event.target); openFromURL(); });
    window.addEventListener('storage', event => {
      if (event.key !== STORAGE_KEY && event.key !== null) return;
      selections = event.key === null ? [] : parseSelections(event.newValue);
      renderSelections();
    });
    window.addEventListener('popstate', () => {
      const params = new URLSearchParams(location.search);
      all('[data-fabric-library]').forEach(library => {
        const state = libraryStates.get(library);
        if (!state) return;
        state.filter = series.has(params.get('filter')) ? params.get('filter') : 'all';
        if (state.input) state.input.value = params.get('q') || '';
        applyFilter(library);
      });
      if (params.has('fabric')) openFromURL();
      else all('[data-fabric-dialog][open]').forEach(dialog => dialog.close());
    });
    openFromURL();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
